import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import type { GameDTO, PickLeagueDTO } from '@pickem/shared';
import { FocusShell } from '../components/Layout';
import { MatchupCard } from '../components/MatchupCard';
import { Spinner, ErrorBanner, EmptyState, Card } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatKickoffTime, timeUntil } from '../lib/format';

interface PickFormData {
  week: {
    id: string;
    displayName: string;
    weekNumber: number;
    opensAt: string | null;
    locksAt: string | null;
    submissionsOpen: boolean;
  };
  league: PickLeagueDTO;
  games: GameDTO[];
  tiebreakerGameId: string | null;
  entry: {
    submitted: boolean;
    tiebreakerPrediction: number | null;
    picks: { gameId: string; selectedTeamId: string }[];
  };
  serverTime: string;
}

type Stage = 'pick' | 'review' | 'done';

export function PickFormPage() {
  const { token } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState<PickFormData | null>(null);
  const [loadError, setLoadError] = useState<{ message: string; kind: 'invalid' | 'other' } | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  const [selections, setSelections] = useState<Record<string, string>>({});
  const [tiebreaker, setTiebreaker] = useState<string>('');
  const [stage, setStage] = useState<Stage>('pick');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Gate this capability URL, always preserving it as `next` so the user lands
  // back on these picks afterward:
  //   • not signed in            → login
  //   • signed in, profile blank → finish profile (e.g. a brand-new Google
  //                                account that hasn't accepted terms / 21+ yet)
  // This route is public (no RequireAuth wrapper), so the onboarding gate that
  // guards the rest of the app doesn't run here — PickForm must enforce it too.
  useEffect(() => {
    if (authLoading || !user) {
      if (!authLoading && !user) {
        const next = encodeURIComponent(location.pathname + location.search);
        navigate(`/login?next=${next}`, { replace: true });
      }
      return;
    }
    if (user.needsProfile) {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/complete-profile?next=${next}`, { replace: true });
    }
  }, [authLoading, user, navigate, location]);

  useEffect(() => {
    if (!user || user.needsProfile) return;
    let active = true;
    setLoading(true);
    api
      .get<PickFormData>(`/picks/w/${token}`)
      .then((d) => {
        if (!active) return;
        setData(d);
        const initial: Record<string, string> = {};
        d.entry.picks.forEach((p) => (initial[p.gameId] = p.selectedTeamId));
        setSelections(initial);
        setTiebreaker(
          d.entry.tiebreakerPrediction != null ? String(d.entry.tiebreakerPrediction) : '',
        );
      })
      .catch((e) => {
        if (!active) return;
        if (e instanceof ApiError && e.status === 404) {
          setLoadError({ message: e.message, kind: 'invalid' });
        } else if (e instanceof ApiError && e.requiresAuth) {
          const next = encodeURIComponent(location.pathname + location.search);
          navigate(`/login?next=${next}`, { replace: true });
        } else {
          setLoadError({
            message: e instanceof ApiError ? e.message : 'Unable to load this week.',
            kind: 'other',
          });
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [user, token, navigate, location]);

  const allPicked = useMemo(
    () => data != null && data.games.every((g) => selections[g.id]),
    [data, selections],
  );
  const tbValid =
    tiebreaker !== '' && Number.isInteger(Number(tiebreaker)) && Number(tiebreaker) >= 0;
  const canReview = allPicked && tbValid;

  if (authLoading || (loading && !loadError)) {
    return (
      <FocusShell>
        <Spinner label="Loading this week’s games…" />
      </FocusShell>
    );
  }

  if (loadError) {
    return (
      <FocusShell>
        <EmptyState
          title={loadError.kind === 'invalid' ? 'Link not valid' : 'Something went wrong'}
        >
          {loadError.kind === 'invalid'
            ? 'This pick link is invalid or has been revoked. Scan the current QR code at Lost Grove to make your picks.'
            : loadError.message}
        </EmptyState>
        <div className="mt-4 text-center">
          <Link to="/" className="text-sm text-grove underline">
            Go to my dashboard
          </Link>
        </div>
      </FocusShell>
    );
  }

  if (!data) return null;

  const closed = !data.week.submissionsOpen;

  // Submissions closed: show a friendly explanation (and their picks if any).
  if (closed && data.entry.submitted) {
    return (
      <FocusShell>
        <WeekHeader
          league={data.league.name}
          title={data.week.displayName}
          subtitle="Picks are locked"
        />
        <Card className="mt-4">
          <p className="text-sm text-muted">
            Picks for {data.week.displayName} are closed. Here’s what you submitted:
          </p>
          <ReviewList data={data} selections={selections} tiebreaker={tiebreaker} />
        </Card>
        <div className="mt-4 text-center">
          <Link to={`/results/${data.week.id}`} className="text-sm text-grove underline">
            See the weekly results →
          </Link>
        </div>
      </FocusShell>
    );
  }

  if (closed && !data.entry.submitted) {
    return (
      <FocusShell>
        <EmptyState title="Picks are closed">
          Picks for {data.week.displayName} are closed. Come back next week and scan the QR code at
          Lost Grove!
        </EmptyState>
      </FocusShell>
    );
  }

  async function submit() {
    if (!data) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      await api.post(`/picks/w/${token}`, {
        picks: data.games.map((g) => ({ gameId: g.id, selectedTeamId: selections[g.id] })),
        tiebreakerPrediction: Number(tiebreaker),
      });
      setStage('done');
    } catch (e) {
      setSubmitError(e instanceof ApiError ? e.message : 'Unable to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (stage === 'done') {
    return (
      <FocusShell>
        <div className="card mt-2 flex flex-col items-center gap-3 px-6 py-10 text-center">
          <img src="/brand/favicon.svg" alt="" aria-hidden className="h-12 w-12" />
          <h1 className="font-display text-4xl uppercase text-grove">You’re in!</h1>
          <p className="text-sm text-muted">
            Your picks for {data.week.displayName} are locked in. Good luck!
          </p>
          {data.week.locksAt && (
            <p className="text-sm">
              You can change them at this same link until{' '}
              <span className="font-medium">{formatDateTime(data.week.locksAt)}</span>.
            </p>
          )}
          <div className="mt-2 flex gap-3">
            <button className="btn-outline" onClick={() => setStage('pick')}>
              Edit picks
            </button>
            <Link to="/" className="btn-primary">
              Go to dashboard
            </Link>
          </div>
        </div>

        <BartenderCheckIn league={data.league} />
        <HowScoringWorks league={data.league} />
      </FocusShell>
    );
  }

  const tiebreakerGame = data.games.find((g) => g.id === data.tiebreakerGameId) ?? null;

  return (
    <FocusShell>
      <WeekHeader
        league={data.league.name}
        title={data.week.displayName}
        subtitle={data.entry.submitted ? 'Editing your picks' : 'Make your picks'}
      />
      {data.week.locksAt && (
        <p className="mb-4 text-center text-sm text-muted">
          Picks lock {formatDateTime(data.week.locksAt)}{' '}
          <span className="text-ember">({timeUntil(data.week.locksAt)})</span>
        </p>
      )}

      {stage === 'pick' && (
        <>
          <div className="space-y-3">
            {data.games.map((g, i) => (
              <MatchupCard
                key={g.id}
                game={g}
                index={i}
                selectedTeamId={selections[g.id] ?? null}
                onSelect={(teamId) => setSelections((s) => ({ ...s, [g.id]: teamId }))}
                isTiebreaker={g.id === data.tiebreakerGameId}
              />
            ))}
          </div>

          {tiebreakerGame && (
            <Card className="mt-4">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-dark">
                Sunday Night Football Tiebreaker
              </div>
              <div className="font-bold">
                {tiebreakerGame.awayTeam.displayName} @ {tiebreakerGame.homeTeam.displayName}
              </div>
              <div className="mb-3 text-sm text-muted">
                Kickoff {formatKickoffTime(tiebreakerGame.kickoff)}
              </div>
              <label htmlFor="tiebreaker" className="label">
                What will the combined final score be?
              </label>
              <input
                id="tiebreaker"
                type="number"
                inputMode="numeric"
                min={0}
                max={200}
                className="input text-center text-2xl font-bold"
                placeholder="e.g. 42"
                value={tiebreaker}
                onChange={(e) => setTiebreaker(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted">
                Add both teams’ scores together (e.g. 27 + 24 = 51).
              </p>
            </Card>
          )}

          <div className="sticky bottom-3 mt-5">
            <button
              className="btn-accent btn-lg w-full shadow-pop"
              disabled={!canReview}
              onClick={() => setStage('review')}
            >
              {canReview
                ? 'Review picks'
                : `Pick all ${data.games.length} games${tbValid ? '' : ' + tiebreaker'}`}
            </button>
          </div>
        </>
      )}

      {stage === 'review' && (
        <Card className="mt-1">
          <h2 className="font-display text-2xl uppercase text-ink">Review your picks</h2>
          <ReviewList data={data} selections={selections} tiebreaker={tiebreaker} />
          {submitError && (
            <div className="mt-3">
              <ErrorBanner message={submitError} />
            </div>
          )}
          <div className="mt-4 flex flex-col gap-2">
            <button className="btn-accent btn-lg w-full" disabled={submitting} onClick={submit}>
              {submitting
                ? 'Submitting…'
                : data.entry.submitted
                  ? 'Update my picks'
                  : 'Submit my picks'}
            </button>
            <button className="btn-ghost w-full" onClick={() => setStage('pick')}>
              Back to edit
            </button>
          </div>
        </Card>
      )}
    </FocusShell>
  );
}

function WeekHeader({
  league,
  title,
  subtitle,
}: {
  league?: string;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="mb-2 text-center">
      {league && (
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-grove/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-grove">
          <span aria-hidden>📍</span> Lost Grove · {league}
        </div>
      )}
      <div className="text-xs font-semibold uppercase tracking-widest text-muted">{subtitle}</div>
      <h1 className="font-display text-4xl uppercase text-grove">{title}</h1>
    </div>
  );
}

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th", … */
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

/**
 * Above-the-fold reminder on the confirmation page: bonus points require a
 * bartender check-in, and here's the Lost Grove location for this league.
 */
function BartenderCheckIn({ league }: { league: PickLeagueDTO }) {
  const mapsUrl = league.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `Lost Grove Brewing ${league.address}`,
      )}`
    : null;
  return (
    <div className="card mt-4 border-l-4 border-amber px-5 py-5 text-left">
      <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-amber-dark">
        Don’t forget your bonus
      </div>
      <h2 className="font-display text-2xl uppercase text-grove">Check in with the bartender</h2>
      <p className="mt-2 text-sm text-body">
        Bonus points aren’t automatic — swing by the bar and check in with your bartender during the
        Sunday games to claim{' '}
        {league.weeklyBonusPoints > 0 ? (
          <>
            your <span className="font-semibold">{league.weeklyBonusPoints} bonus points</span>
          </>
        ) : (
          'your weekly bonus points'
        )}
        . No check-in, no bonus.
      </p>
      <div className="mt-4 rounded-lg bg-cream px-4 py-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted">
          You’re playing at
        </div>
        <div className="font-display text-xl uppercase text-grove">Lost Grove · {league.name}</div>
        {league.address && (
          <>
            <div className="mt-0.5 text-sm text-body">{league.address}</div>
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-sm font-medium text-ember underline"
              >
                Get directions →
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Explains how the weekly and season scoring works, using this league's own
 * placement table and bonus value so the numbers are always accurate.
 */
function HowScoringWorks({ league }: { league: PickLeagueDTO }) {
  const hasRules = league.placementRules.length > 0;
  return (
    <div className="card mt-4 px-5 py-5 text-left">
      <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted">
        Good to know
      </div>
      <h2 className="font-display text-2xl uppercase text-grove">How scoring works</h2>

      <ol className="mt-3 space-y-3 text-sm text-body">
        <li>
          <span className="font-semibold text-grove">Pick the winners.</span> Every game you call
          correctly counts toward your record for the week. Most correct picks sits at the top.
        </li>
        <li>
          <span className="font-semibold text-grove">Finish high, earn league points.</span>{' '}
          {hasRules ? (
            <>
              Where you land each week earns points toward the season standings:
              <div className="mt-2 overflow-hidden rounded-lg border border-line">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-cream text-xs uppercase tracking-wide text-muted">
                      <th className="px-3 py-1.5 text-left font-semibold">Weekly finish</th>
                      <th className="px-3 py-1.5 text-right font-semibold">League points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {league.placementRules.map((r) => (
                      <tr key={r.placement} className="border-t border-line">
                        <td className="px-3 py-1.5">{ordinal(r.placement)} place</td>
                        <td className="px-3 py-1.5 text-right font-bold text-grove">{r.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            'The higher you finish each week, the more league points you earn toward the season standings.'
          )}
        </li>
        <li>
          <span className="font-semibold text-grove">Ties go to the tiebreaker.</span> Predict the
          combined final score of Sunday Night Football — the closest guess wins the tie.
        </li>
        <li>
          <span className="font-semibold text-grove">Grab your bonus at the bar.</span> Checking in
          with your bartender on Sundays adds{' '}
          {league.weeklyBonusPoints > 0 ? (
            <span className="font-semibold">{league.weeklyBonusPoints} bonus points</span>
          ) : (
            'bonus points'
          )}{' '}
          straight on top of what you earn.
        </li>
      </ol>

      <p className="mt-4 rounded-lg bg-grove/5 px-4 py-3 text-sm text-body">
        <span className="font-semibold text-grove">Season standings</span> add up your league points
        and bonus points all season long. Rack up good weeks — and don’t skip the bar.
      </p>
    </div>
  );
}

function ReviewList({
  data,
  selections,
  tiebreaker,
}: {
  data: PickFormData;
  selections: Record<string, string>;
  tiebreaker: string;
}) {
  return (
    <ul className="mt-3 divide-y divide-line">
      {data.games.map((g, i) => {
        const pickId = selections[g.id];
        const team = pickId === g.homeTeam.id ? g.homeTeam : g.awayTeam;
        return (
          <li key={g.id} className="flex items-center justify-between py-2">
            <span className="text-sm text-muted">
              {i + 1}. {g.awayTeam.abbreviation} @ {g.homeTeam.abbreviation}
              {g.id === data.tiebreakerGameId && (
                <span className="ml-2 chip bg-amber/20 text-amber-dark">SNF</span>
              )}
            </span>
            <span className="font-bold">{team?.displayName ?? '—'}</span>
          </li>
        );
      })}
      <li className="flex items-center justify-between py-2">
        <span className="text-sm font-semibold uppercase text-amber-dark">
          Tiebreaker (combined)
        </span>
        <span className="font-display text-2xl text-grove">{tiebreaker || '—'}</span>
      </li>
    </ul>
  );
}
